import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';

// Order statuses that count as a "user-facing notification" — these are the
// states the customer was pushed about and would expect to see in the app.
const NOTIFY_STATUSES = ['ready', 'cancelled'];

const POLL_INTERVAL_MS = 60_000;

// Format a Date as "YYYY-MM-DD HH:MM:SS" in local time, matching the format
// SQLite returns for `datetime('now','localtime')` so we can string-compare
// directly without any timezone gymnastics.
function formatLocalNow() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

function storageKeyFor(userId) {
  return userId ? `order-notifications-seen-${userId}` : null;
}

export function useOrderNotifications() {
  const { user, isLoggedIn } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const userId = user?.id;
  const storageKey = storageKeyFor(userId);

  const markAllSeen = useCallback(() => {
    if (!storageKey) return;
    localStorage.setItem(storageKey, formatLocalNow());
    setUnreadCount(0);
  }, [storageKey]);

  const refresh = useCallback(async () => {
    if (!isLoggedIn || !storageKey) {
      setUnreadCount(0);
      return;
    }

    let lastSeen = localStorage.getItem(storageKey);
    if (!lastSeen) {
      // First ever read for this user on this device — anchor to "now" so
      // pre-existing ready/cancelled orders don't all flood in as unread.
      lastSeen = formatLocalNow();
      localStorage.setItem(storageKey, lastSeen);
    }

    try {
      const orders = await api.getMyOrders();
      const count = orders.filter(
        (o) =>
          NOTIFY_STATUSES.includes(o.status) &&
          typeof o.updated_at === 'string' &&
          o.updated_at > lastSeen
      ).length;
      setUnreadCount(count);
    } catch {
      // Network blip — keep the previous count rather than flicker to 0.
    }
  }, [isLoggedIn, storageKey]);

  useEffect(() => {
    refresh();

    const interval = setInterval(refresh, POLL_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Fired by nativePush.js when an APNs/FCM push arrives in the foreground.
    const onPushRefresh = () => refresh();
    window.addEventListener('app:notifications-refresh', onPushRefresh);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('app:notifications-refresh', onPushRefresh);
    };
  }, [refresh]);

  // Wipe any other users' baselines when this user logs out, so stale
  // counts can't surface for the next account that signs in on this device.
  useEffect(() => {
    if (!isLoggedIn) setUnreadCount(0);
  }, [isLoggedIn]);

  return { unreadCount, markAllSeen, refresh };
}
